import React from 'react';
import Link from 'next/link';
import Button from '@common/button';
import Text from '@common/text';
import Icon from '@common/assets';
import { useToast } from '@common/toast';
import { useNHISAuthCheck } from '@repository/nhis/useNHIS';
import { FontStyle } from '@domain/constant/fontStyle';
import { NhisProviderHelpUrlMap, NhisProviderTextMap, NhisProviderType } from '@domain/constant/nhis/NHISProviderType';
import classnames from 'classnames/bind';
import styles from './index.module.scss';

const cx = classnames.bind(styles);

interface Step3Props {
  prevStep: () => void;
  nextStep: () => void;
  provider: NhisProviderType | null;
}

const Step3 = ({ prevStep, nextStep, provider }: Step3Props) => {
  const { mutateAsync } = useNHISAuthCheck();
  const Toast = useToast();

  const providerName = provider ? NhisProviderTextMap[provider] : '';
  const providerHelpUrl = provider ? NhisProviderHelpUrlMap[provider] : '';

  const handleClick = async () => {
    await mutateAsync(undefined, undefined)
      .then(() => {
        nextStep();
      })
      .catch((e) => {
        const errorCode = e?.response?.data?.errorCode;
        switch (errorCode) {
          case 'B901':
            Toast({
              type: 'error',
              iconType: 'info',
              content: '인증 중에 오류가 발생했어요. 절차를 다시 확인해주세요.',
            });
            return;
          case 'B902':
            Toast({ type: 'error', iconType: 'info', content: '인증이 완료되지 않았어요. 절차를 다시 확인해주세요.' });
            return;
          case 'B903':
            Toast({ type: 'error', iconType: 'info', content: '인증 시간이 만료되었어요. 다시 시도해주세요.' });
            return;
          default:
            Toast({
              type: 'error',
              iconType: 'info',
              content: '인증 중에 오류가 발생했어요. 절차를 다시 확인해주세요.',
            });
            return;
        }
      });
  };

  return (
    <div className={cx('area')}>
      <div className={cx('wrap')}>
        <em className={cx('desc')}>
          인증 요청을 보냈어요.
          <br />
          남은 인증 절차를 진행해주세요!
        </em>

        <ul className={cx('listArea')}>
          <li className={cx('itemArea')}>
            <Text fontStyle={FontStyle.B2_M}>1. {providerName} 앱에서 인증 요청 메시지를 확인해주세요.</Text>
          </li>
          <li className={cx('itemArea')}>
            <Text fontStyle={FontStyle.B2_M}>2. 인증 완료 후, 하단의 ‘인증 완료하기’ 버튼을 눌러주세요.</Text>
          </li>
        </ul>

        <div className={cx('infoArea')}>
          <Icon name={'informationLight'} width={20} height={20} />
          <Text element={'div'} className={cx('infoText')}>
            인증서 문제는{' '}
            <Link href={providerHelpUrl} target={'_blank'} className={cx('link')}>
              {providerName} 고객 센터
            </Link>
            를 통해 빠르게 문제를 해결할 수 있어요.
          </Text>
        </div>
      </div>

      <div className={cx('btnArea')}>
        <button className={cx('prevBtn')} onClick={prevStep}>
          이전
        </button>
        <Button className={cx('btn')} type={'button'} onClick={handleClick} disabled={false}>
          인증 완료하기
        </Button>
      </div>
    </div>
  );
};

export default Step3;
